import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getModule, listModules, ttsAudioUrl } from "@/lib/apiClient";
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, WifiOff, Volume2, VolumeX } from "lucide-react";
import { getDownloadedModule, resolveAudioSrc } from "@/lib/offlineCache";
import { resolveModuleName, resolveModuleIntro, resolveModuleSubtitle } from "@/lib/i18nContent";
import { useOffline } from "@/contexts/OfflineContext";
import { useI18n } from "@/contexts/I18nContext";
import ModuleInstructionsHelp from "@/components/ModuleInstructionsHelp";
import ReadingMode from "./modules/ReadingMode";
import SoundMcqMode from "./modules/SoundMcqMode";
import ReadingModeRecording from "./modules/ReadingModeRecording";

/**
 * Unified Module Screen — dispatches to the right mode based on content_type:
 *  - "reading"   → modules 1–7 (Learn / Start timer / Stop / Result · Practice)
 *  - "mcq"       → module 8 (sound vocab listen + pick)
 *  - "recording" → module 9 (record / upload final recitation)
 *
 * Falls back to IndexedDB cache when the network call fails (offline).
 */
export default function ModuleScreen() {
  const { t } = useI18n();
  const { code } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [offlineSource, setOfflineSource] = useState(false);
  // True only for the dead-end case: browser reports offline AND this module
  // was never downloaded. Distinct from plain `!data` (still loading) so the
  // render below can show a real retry screen instead of leaving the page
  // blank under the header forever — see the effect and the `if (!data)`
  // branch further down for why this needed its own state.
  const [stuckOffline, setStuckOffline] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [introPlaying, setIntroPlaying] = useState(false);
  const [moduleList, setModuleList] = useState(null);
  const introAudio = useRef(null);
  const { online } = useOffline();

  // Fetched once (module order is stable within a session) to drive the
  // Previous/Next Module nav — independent of the per-code module fetch
  // below, which reloads on every code change / attempt-save.
  useEffect(() => {
    if (!online) return;
    listModules().then(setModuleList).catch(() => {});
  }, [online]);

  // Clear stale data ONLY when navigating to a different module code so the
  // loading placeholder shows cleanly. We deliberately do NOT clear data on
  // reloadKey bumps — those happen mid-attempt (e.g. after submit-attempt
  // succeeds and ReadingMode calls onAttemptSaved) and clearing would
  // unmount ReadingMode, wiping its `result` / `recBlob` / `submitted` state
  // and hiding the post-attempt UI (listen-back + submit + peer-review).
  const lastCode = useRef(code);
  useEffect(() => {
    if (lastCode.current !== code) {
      setData(null);
      setStuckOffline(false);
      lastCode.current = code;
    }
  }, [code]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // When the browser is OFFLINE, never hit the network — Chromium's HTTP
      // cache may otherwise serve a stale /api/modules/<code> response and
      // mask the "not downloaded" UX. Trust IndexedDB exclusively.
      if (!online) {
        const cached = await getDownloadedModule(code);
        if (cancelled) return;
        if (cached) {
          setData({ module: cached, progress: {}, sound_vocab_passing_pct: 100 });
          setOfflineSource(true);
          setStuckOffline(false);
        } else {
          // Dead end otherwise: `data` never gets set, so without stuckOffline
          // the page would sit blank under the header forever once this toast
          // fades — see the `if (!data)` render branch and retryDirectFetch
          // below for why this needs its own persistent screen + a real retry
          // rather than just re-reading `online` again (which may itself be
          // wrong — see retryDirectFetch's comment).
          toast.error(t("ms.toast_not_offline"));
          setStuckOffline(true);
        }
        return;
      }

      // Online path — normal fetch with IndexedDB as the network-failure fallback.
      try {
        const fresh = await getModule(code);
        if (!cancelled) { setData(fresh); setOfflineSource(false); setStuckOffline(false); }
      } catch (_e) {
        const cached = await getDownloadedModule(code);
        if (cancelled) return;
        if (cached) {
          setData({ module: cached, progress: {}, sound_vocab_passing_pct: 100 });
          setOfflineSource(true);
          setStuckOffline(false);
          toast.message(t("ms.toast_cached_version"));
        } else {
          toast.error(t("ms.toast_unavailable"));
        }
      }
    })();
    return () => { cancelled = true; };
    // `t` is left out on purpose: it's only used for toast copy here, and
    // including it would re-run the whole module fetch on every language
    // switch. DigitalOcean builds with CI=true, where react-scripts promotes
    // this warning to a hard error, so it can't just be left standing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, reloadKey, online]);

  // Manual retry from the stuck-offline dead end. Deliberately does NOT just
  // bump reloadKey: that would re-run the effect above with the *same*
  // `online` value from OfflineContext, which only flips on the browser's own
  // 'online'/'offline' events — and navigator.onLine is known to occasionally
  // misreport (stays false after a network blip actually recovers, VPN/driver
  // quirks, etc.), which was exactly the trap: someone genuinely back online
  // would keep hitting the IndexedDB-only branch forever with no way out
  // short of a full page reload. This instead attempts a real fetch every
  // time the user asks, and trusts what that fetch actually does over
  // whatever `online` currently claims.
  const retryDirectFetch = useCallback(async () => {
    setRetrying(true);
    try {
      const fresh = await getModule(code);
      setData(fresh);
      setOfflineSource(false);
      setStuckOffline(false);
    } catch (_e) {
      const cached = await getDownloadedModule(code);
      if (cached) {
        setData({ module: cached, progress: {}, sound_vocab_passing_pct: 100 });
        setOfflineSource(true);
        setStuckOffline(false);
      } else {
        toast.error(t("ms.toast_not_offline"));
      }
    } finally {
      setRetrying(false);
    }
  }, [code, t]);

  // Stop the intro audio if it's playing (e.g. user navigates away or starts).
  // Defined with useCallback so the autoplay + unmount effects below have a
  // stable reference and so the function exists during the temporal-dead-zone
  // window before `data` resolves.
  const stopIntro = useCallback(() => {
    if (introAudio.current) {
      try { introAudio.current.pause(); } catch (_e) { /* harmless */ }
      introAudio.current = null;
    }
    setIntroPlaying(false);
  }, []);

  // Plays the module intro narration — press-to-play only, no autoplay.
  const playIntro = useCallback(async () => {
    const mod = data?.module;
    if (!mod?.intro) return;
    if (introPlaying) { stopIntro(); return; }
    const src = await resolveAudioSrc(ttsAudioUrl(resolveModuleIntro(t, mod)));
    const a = new Audio(src);
    introAudio.current = a;
    a.onended = () => { setIntroPlaying(false); introAudio.current = null; };
    setIntroPlaying(true);
    a.play().catch(() => { setIntroPlaying(false); introAudio.current = null; });
  }, [data, introPlaying, stopIntro, t]);

  // Intro narration is press-to-play only — no autoplay. The student taps
  // the intro icon (or the "Listen to Audio" stripe) when they want it.

  // Always pause intro narration on unmount so it doesn't bleed into the
  // next route.
  useEffect(() => () => stopIntro(), [stopIntro]);

  if (!data) {
    // stuckOffline: the dead end this whole block exists for — offline (or
    // wrongly believed to be) with nothing downloaded for this module. A
    // bare header used to be all that rendered here, with the only
    // explanation being a toast that had already faded by the time anyone
    // looked back at the screen. Now it's a real, persistent state with a
    // retry that actually re-attempts the network rather than re-trusting
    // the same `online` flag that got them stuck here.
    if (stuckOffline) {
      return (
        <div className="min-h-screen flex flex-col" data-testid="module-offline-dead-end">
          <Header />
          <main className="flex-1 max-w-[448px] mx-auto w-full px-5 py-16 flex flex-col items-center text-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-[#FEF2F2] text-[#7F1D1D] grid place-items-center">
              <WifiOff className="w-6 h-6" aria-hidden="true" />
            </div>
            <p className="text-[#4B5563] leading-relaxed">{t("ms.toast_not_offline")}</p>
            <button
              type="button"
              onClick={retryDirectFetch}
              disabled={retrying}
              data-testid="module-offline-retry"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#064E3B] text-white text-sm font-medium transition hover:bg-[#047857] disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D97706] focus-visible:ring-offset-2">
              {retrying ? t("common.loading") : t("common.try_again")}
            </button>
          </main>
          <Footer />
        </div>
      );
    }
    return <div className="min-h-screen"><Header /></div>;
  }
  const m = data.module;
  const reload = () => setReloadKey((k) => k + 1);

  const idx = moduleList ? moduleList.findIndex((x) => x.code === code) : -1;
  const prevModule = idx > 0 ? moduleList[idx - 1] : null;
  const nextModule = idx >= 0 && idx < (moduleList?.length || 0) - 1 ? moduleList[idx + 1] : null;

  return (
    <div className="min-h-screen flex flex-col" data-testid={`module-screen-${m.code}`}>
      <Header />
      <main className="flex-1 max-w-[896px] mx-auto w-full px-5 sm:px-8 py-10">
        <div className="flex items-center justify-between gap-2 flex-wrap" data-testid="module-nav-row">
          <button
            onClick={() => prevModule && nav(`/modules/${prevModule.code}`)}
            disabled={!prevModule}
            className="inline-flex items-center gap-1 text-sm text-[#4B5563] hover:text-[#064E3B] transition disabled:opacity-0 disabled:pointer-events-none"
            data-testid="prev-module" title={prevModule ? `${t("ms.prev_module")}: ${resolveModuleName(t, prevModule)}` : undefined}>
            {/* Always the LTR-natural icon — index.css mirrors Chevron/Arrow
                icons for [dir="rtl"] globally now, so picking a direction
                here too would flip it right back to the wrong way. */}
            <ChevronLeft className="w-4 h-4" /> <span className="hidden sm:inline">{t("ms.prev_module")}</span>
          </button>
          <button onClick={() => nav("/modules")}
            className="inline-flex items-center gap-1 text-sm text-[#4B5563] hover:text-[#064E3B] transition" data-testid="back-modules">
            <ArrowLeft className="w-4 h-4" /> {t("ms.all_modules")}
          </button>
          <button
            onClick={() => nextModule && nav(`/modules/${nextModule.code}`)}
            disabled={!nextModule}
            className="inline-flex items-center gap-1 text-sm text-[#4B5563] hover:text-[#064E3B] transition disabled:opacity-0 disabled:pointer-events-none"
            data-testid="next-module" title={nextModule ? `${t("ms.next_module")}: ${resolveModuleName(t, nextModule)}` : undefined}>
            <span className="hidden sm:inline">{t("ms.next_module")}</span> <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {(!online || offlineSource) && (
          <div data-testid="module-offline-banner" className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium bg-[#FEF2F2] text-[#7F1D1D] border border-[#FECACA]">
            <WifiOff className="w-3.5 h-3.5" /> {t("ms.offline_banner")}
          </div>
        )}

        <div className="mt-4 flex items-start gap-5">
          <div className="w-14 h-14 rounded-2xl grid place-items-center font-mono text-base font-bold text-white shrink-0" style={{ background: m.color }}>
            {String(m.n).padStart(2, "0")}
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-[0.22em] text-[#4B5563]">{t("dashboard.module_n", { n: m.n })}</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-1 text-[#1F2937] flex items-center gap-3">
              <span>{resolveModuleName(t, m)}</span>
              {data.progress?.passed ? (
                <span data-testid={`module-light-pass-${m.code}`}
                  title={t("modules.light_pass_title")}
                  className="inline-block w-3 h-3 rounded-full bg-[#10B981] shadow-[0_0_0_4px_rgba(16,185,129,0.18)]" />
              ) : (data.progress?.attempts || 0) > 0 ? (
                <span data-testid={`module-light-fail-${m.code}`}
                  title={t("modules.light_fail_title")}
                  className="inline-block w-3 h-3 rounded-full bg-[#EF4444] shadow-[0_0_0_4px_rgba(239,68,68,0.18)]" />
              ) : null}
            </h1>
            <div className={resolveModuleSubtitle(t, m) === m.name_ar ? "font-arabic text-lg text-[#4B5563]" : "text-lg text-[#4B5563]"}>{resolveModuleSubtitle(t, m)}</div>
          </div>
          {m.target_seconds > 0 && (
            <div className="rounded-2xl bg-white border border-[#E7E0D2] px-4 py-3 text-center" data-testid="target-time">
              <div className="text-[10px] uppercase tracking-widest text-[#4B5563]">{t("ms.target_time")}</div>
              <div className="font-heading text-2xl text-[#064E3B] tabular-nums">{m.target_seconds}s</div>
            </div>
          )}
        </div>

        <ModuleInstructionsHelp module={m} />

        {!m.hide_intro && (
          <p className="mt-6 text-[#4B5563] leading-relaxed flex items-start gap-3">
            <button
              data-testid="intro-replay-btn"
              onClick={playIntro}
              title={introPlaying ? t("ms.intro_stop_title") : t("ms.intro_play_title")}
              className={`shrink-0 mt-0.5 w-9 h-9 rounded-full grid place-items-center border transition ${
                introPlaying
                  ? "bg-[#D97706] border-[#D97706] text-white animate-pulse"
                  : "bg-white border-[#E7E0D2] text-[#4B5563] hover:border-[#D97706] hover:text-[#D97706]"
              }`}>
              {introPlaying ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <span className="flex-1">{resolveModuleIntro(t, m)}</span>
          </p>
        )}

        <div className="mt-8">
          {m.content_type === "reading" && (
            <ReadingMode module={m} progress={data.progress} onAttemptSaved={reload} />
          )}
          {m.content_type === "mcq" && (
            <SoundMcqMode module={m} passingPct={data.sound_vocab_passing_pct} onAttemptSaved={reload} />
          )}
          {m.content_type === "recording" && (
            <ReadingModeRecording module={m} progress={data.progress} onAttemptSaved={reload} />
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
